<template>
  <template v-if="isOpen">

    <!-- ── Desktop (>719px): MdModal centered dialog ──────────────────── -->
    <MdModal
      v-if="!isBottomSheet"
      class="foundry-contract-popup"
      :showoverlay="true"
      :isopen="isOpen"
      width="776px"
      :enablebackdropclick="false"
      data-qa-id="foundry-contract-popup"
      @handleclose="handleClose"
    >
      <div slot="header">
        <MdTypography
          tag="h5"
          textalign="start"
          color="spirits--900"
          variant="heading5--medium"
          data-qa-id="foundry-contract-popup-title"
        >
          {{ title }}
        </MdTypography>
      </div>

      <div slot="body">
        <FoundryContractContent
          :isAdmin="isAdmin"
          :sections="sections"
          :learnMoreLinks="learnMoreLinks"
          :showFooter="false"
        />
      </div>

      <div slot="footer">
        <div class="fcp-popup__footer-actions">
          <MdButton
            size="large"
            variant="none"
            color="lightgrey"
            type="button"
            :label="$t('foundry_contract_popup.btn_close')"
            data-qa-id="foundry-contract-popup-btn-close"
            @handleclick="handleClose"
          >
            {{ $t('foundry_contract_popup.btn_close') }}
          </MdButton>
          <MdButton
            v-if="showSeeChangesButton"
            size="large"
            variant="none"
            color="lightgrey"
            type="button"
            :label="$t('foundry_contract_popup.btn_see_changes')"
            data-qa-id="foundry-contract-popup-btn-see-changes"
            @handleclick="$emit('seeChanges')"
          >
            {{ $t('foundry_contract_popup.btn_see_changes') }}
          </MdButton>
          <MdButton
            v-if="showSignAgreementButton"
            size="large"
            variant="none"
            color="blue"
            type="button"
            :label="$t('foundry_contract_popup.btn_sign_agreement')"
            data-qa-id="foundry-contract-popup-btn-sign"
            @handleclick="$emit('signAgreement')"
          >
            <MdIconAgreements slot="icon-start" size="16px" color="white" />
            {{ $t('foundry_contract_popup.btn_sign_agreement') }}
          </MdButton>
        </div>
      </div>
    </MdModal>

    <!-- ── Tablet/Mobile (≤1279px): native bottom-sheet overlay ─────────── -->
    <!-- We own this element entirely — no shadow DOM cascade fights.       -->
    <div
      v-else
      class="fcp-sheet"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      data-qa-id="foundry-contract-popup"
    >
      <!-- Semi-transparent backdrop; click to close, touch-move blocked to prevent body scroll -->
      <div class="fcp-sheet__overlay" @click="handleClose" @touchmove.prevent />

      <!-- Group: close button (tablet only) sits above the panel in a column -->
      <div class="fcp-sheet__group">

        <!-- Close button: white circle above the panel, right-aligned (mobile + tablet) -->
        <MdButton
          class="fcp-sheet__close-btn"
          size="small"
          variant="none"
          color="white"
          type="button"
          icononly
          :label="$t('foundry_contract_popup.btn_close')"
          data-qa-id="foundry-contract-popup-btn-sheet-close"
          @handleclick="handleClose"
        >
          <MdIconClose size="16px" color="spirits--600" />
        </MdButton>

        <!-- The sliding panel -->
        <div class="fcp-sheet__panel">

          <!-- Header: title only (close button is outside the panel) -->
          <div class="fcp-sheet__header">
            <MdTypography
              tag="h5"
              textalign="start"
              color="spirits--900"
              variant="heading5--medium"
              data-qa-id="foundry-contract-popup-title"
            >
              {{ title }}
            </MdTypography>
          </div>

          <!-- Scrollable body -->
          <div class="fcp-sheet__body">
            <FoundryContractContent
              :isAdmin="isAdmin"
              :sections="sections"
              :learnMoreLinks="learnMoreLinks"
              :showFooter="false"
            />
          </div>

          <!-- Footer: horizontal on tablet, stacked on mobile -->
          <div class="fcp-sheet__footer">
            <MdButton
              v-if="!isMobileView"
              size="large"
              variant="none"
              color="lightgrey"
              type="button"
              class="fcp-sheet__btn"
              :label="$t('foundry_contract_popup.btn_close')"
              data-qa-id="foundry-contract-popup-btn-close"
              @handleclick="handleClose"
            >
              {{ $t('foundry_contract_popup.btn_close') }}
            </MdButton>
            <MdButton
              v-if="showSeeChangesButton"
              size="large"
              variant="none"
              color="lightgrey"
              type="button"
              class="fcp-sheet__btn"
              :label="$t('foundry_contract_popup.btn_see_changes')"
              data-qa-id="foundry-contract-popup-btn-see-changes"
              @handleclick="$emit('seeChanges')"
            >
              {{ $t('foundry_contract_popup.btn_see_changes') }}
            </MdButton>
            <MdButton
              v-if="showSignAgreementButton"
              size="large"
              variant="none"
              color="blue"
              type="button"
              class="fcp-sheet__btn"
              :label="$t('foundry_contract_popup.btn_sign_agreement')"
              data-qa-id="foundry-contract-popup-btn-sign"
              @handleclick="$emit('signAgreement')"
            >
              <MdIconAgreements slot="icon-start" size="16px" color="white" />
              {{ $t('foundry_contract_popup.btn_sign_agreement') }}
            </MdButton>
          </div>

        </div><!-- /fcp-sheet__panel -->
      </div><!-- /fcp-sheet__group -->
    </div><!-- /fcp-sheet -->

  </template>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { MdModal, MdButton, MdTypography, MdIconAgreements, MdIconClose } from '@antiqua-dls/vue-components';
import { disableScrollOnModalOpen } from 'src/utils/helper/misc';
import FoundryContractContent from './FoundryContractContent.vue';

const { t } = useI18n();

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  isAdmin: {
    type: Boolean,
    required: true
  },
  titleKey: {
    type: String,
    required: true
  },
  titleParams: {
    type: Object,
    default: () => ({})
  },
  sections: {
    type: Array,
    required: true,
    validator: (val) => val.length >= 1 && val.length <= 2
  },
  learnMoreLinks: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close', 'seeChanges', 'signAgreement']);

// ── Viewport tracking ──────────────────────────────────────────────────
// Mobile: ≤ 719px | Tablet: 720–1279px | Desktop: ≥ 1280px
const TABLET_BREAKPOINT = 1279; // bottom-sheet for mobile + tablet
const MOBILE_BREAKPOINT = 719;  // mobile-only UI (stacked buttons, no close btn)

const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
const isBottomSheet = computed(() => viewportWidth.value <= TABLET_BREAKPOINT);
const isMobileView  = computed(() => viewportWidth.value <= MOBILE_BREAKPOINT);

const onResize = () => { viewportWidth.value = window.innerWidth; };

// ── Derived state ──────────────────────────────────────────────────────
const title = computed(() => t(props.titleKey, props.titleParams));

const hasTerminatingSection = computed(() =>
  props.sections.some(s => s.status === 'terminating')
);

const showSeeChangesButton = computed(() =>
  props.isAdmin && hasTerminatingSection.value
);

const showSignAgreementButton = computed(() => props.isAdmin);

const handleClose = () => emit('close');

// ── Lifecycle ──────────────────────────────────────────────────────────
onMounted(() => {
  window.addEventListener('resize', onResize);
  if (props.isOpen) {
    disableScrollOnModalOpen(true);       // lock body
    disableScrollOnModalOpen(true, true); // lock html (app may scroll on documentElement)
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  disableScrollOnModalOpen(false);        // restore body
  disableScrollOnModalOpen(false, true);  // restore html
});
</script>

<!-- ── Scoped: desktop MdModal footer only ───────────────────────────── -->
<style lang="scss" scoped>
.fcp-popup {
  &__footer-actions {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    width: 100%;
  }
}
</style>

<!-- ── Unscoped: sheet + MdModal desktop overrides ───────────────────── -->
<!-- Sheet styles are unscoped so they are never blocked by Vue's         -->
<!-- data-v-xxxx attribute selector on child elements.                    -->
<style lang="scss">
@import './stylesheets/foundry-contract-popup';

// ── Desktop MdModal ::part() overrides ────────────────────────────────
.foundry-contract-popup {
  &::part(md-modal__content) {
    gap: 0;
    max-height: 90vh;
  }

  &::part(md-modal__header) {
    padding: 40px 40px 24px 40px;
  }

  &::part(md-modal__body) {
    padding: 24px 40px;
    border-top: 1px solid var(--md-border-grey);
    border-bottom: 1px solid var(--md-border-grey);
    overflow-y: auto;
  }

  &::part(md-modal__footer) {
    padding: 24px 40px 40px 40px;
  }
}

// ── Bottom-sheet overlay (tablet ≤744px / mobile ≤480px) ──────────────
// All styles are nested under .fcp-sheet to prevent global leakage.
.fcp-sheet {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: flex-end;

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    cursor: pointer;
  }

  // Group: stacks close button above panel; both sit above the overlay backdrop
  &__group {
    position: relative;
    z-index: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 16px; // Figma: gap-[16px] between close button row and panel
  }

  // White circle close button — Figma: bg-white, rounded-[36px], p-[8px]
  &__close-btn {
    margin-right: 16px; // Figma: px-[16px] on the Cross container

    &::part(md-button) {
      border-radius: 36px;
    }
  }

  &__panel {
    width: 100%;
    background: var(--md-color--white);
    border-radius: 16px 16px 0 0;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overscroll-behavior: contain;
    box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.12);
  }

  &__header {
    padding: 24px 24px 16px 24px;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    border-top: 1px solid var(--md-border-grey);
    border-bottom: 1px solid var(--md-border-grey);
    -webkit-overflow-scrolling: touch;
    min-height: 0;
  }

  &__footer {
    padding: 24px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 16px;
  }
}

// Mobile (≤480px): same bottom-sheet as tablet, footer buttons stack vertically
@include fcp-mobile {
  .fcp-sheet {
    &__footer {
      flex-direction: column;
      padding-bottom: env(safe-area-inset-bottom, 24px);
    }

    &__btn {
      display: block;
      width: 100%;
    }
  }
}
</style>
